import { useEffect, useState } from "react";
import "../styles/admin-base.css";
import "../styles/audit-log.css";
import {
  ShieldAlert,
  RefreshCw,
  ShieldCheck,
  UserCheck,
  Building2,
  CalendarDays,
  IdCard,
  LifeBuoy,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Filter,
  SlidersHorizontal,
} from "lucide-react";
import { actionTone, formatTimestamp, getActionDescription } from "../components/ai/AuditRow";
import client from "../../src/api/client";

const ACTOR_FILTER_PRESETS = [
  { value: "all", label: "All actors" },
  { value: "admin", label: "Admin" },
  { value: "department", label: "Department" },
  { value: "AI", label: "AI" },
  { value: "system", label: "System" },
];

const ROLE_VISUALS = {
  "Super Administrator": {
    icon: ShieldCheck,
    className: "is-super-admin",
    label: "Super Administrator",
  },
  "Approver Admin": {
    icon: UserCheck,
    className: "is-approver-admin",
    label: "Approver Admin",
  },
  "Booking Admin": {
    icon: Building2,
    className: "is-booking-admin",
    label: "Booking Admin",
  },
  "Events Admin": {
    icon: CalendarDays,
    className: "is-events-admin",
    label: "Events Admin",
  },
  "ID Card Manager": {
    icon: IdCard,
    className: "is-id-card-manager",
    label: "ID Card Manager",
  },
  "Tickets Admin": {
    icon: LifeBuoy,
    className: "is-tickets-admin",
    label: "Tickets Admin",
  },
  Department: {
    icon: Building2,
    className: "is-department",
    label: "Department",
  },
};

function resolveDisplayRoles(entry) {
  const roles = [];

  if (entry?.actorRole && ROLE_VISUALS[entry.actorRole]) {
    roles.push(entry.actorRole);
  }

  if (Array.isArray(entry?.actorRoles)) {
    entry.actorRoles.forEach((role) => {
      if (ROLE_VISUALS[role]) {
        roles.push(role);
      }
    });
  }

  const uniqueRoles = [...new Set(roles)];
  if (uniqueRoles.length > 0) {
    return uniqueRoles;
  }

  if (entry?.actor === "admin") {
    return ["Approver Admin"];
  }

  if (entry?.actor === "department") {
    return ["Department"];
  }

  return [];
}

export default function AuditLog() {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    actor: "all",
    action: "",
    entityType: "",
    dateFrom: "",
    dateTo: "",
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    actions: [],
    entities: [],
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const incomingActions = Array.isArray(meta?.filters?.actions) ? meta.filters.actions : [];
    const incomingEntities = Array.isArray(meta?.filters?.entities) ? meta.filters.entities : [];

    if (incomingActions.length === 0 && incomingEntities.length === 0) {
      return;
    }

    setFilterOptions((current) => {
      const nextActions = [...new Set([...current.actions, ...incomingActions])]
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right));
      const nextEntities = [...new Set([...current.entities, ...incomingEntities])]
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right));

      return {
        actions: nextActions,
        entities: nextEntities,
      };
    });
  }, [meta]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
    }, 350);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  const normalizedSearch = debouncedSearchInput.trim().slice(0, 120);
  const effectiveFilters = {
    actor: filters.actor,
    action: filters.action,
    entityType: filters.entityType,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    search: normalizedSearch,
  };
  const isDebouncingSearch = searchInput.trim() !== normalizedSearch;

  useEffect(() => {
    setPage(1);
  }, [
    effectiveFilters.actor,
    effectiveFilters.action,
    effectiveFilters.entityType,
    effectiveFilters.dateFrom,
    effectiveFilters.dateTo,
    effectiveFilters.search,
  ]);

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setSearchInput("");
    setDebouncedSearchInput("");
    setFilters({
      actor: "all",
      action: "",
      entityType: "",
      dateFrom: "",
      dateTo: "",
    });
    setPage(1);
  };

  const hasActiveFilters =
    normalizedSearch.length > 0
    || filters.actor !== "all"
    || Boolean(filters.action)
    || Boolean(filters.entityType)
    || Boolean(filters.dateFrom)
    || Boolean(filters.dateTo);

  const actorFilterLabel = ACTOR_FILTER_PRESETS.find((preset) => preset.value === filters.actor)?.label ?? "All actors";
  const totalEntries = Number(stats?.total ?? 0);

  useEffect(() => {
    let cancelled = false;

    async function loadAuditLog() {
      setLoading(true);
      setError(null);
      try {
        const response = await client.get("/api/admin/audit/logs", {
          params: {
            page,
            pageSize,
            ...(effectiveFilters.actor !== "all" ? { actor: effectiveFilters.actor } : {}),
            ...(effectiveFilters.action ? { action: effectiveFilters.action } : {}),
            ...(effectiveFilters.entityType ? { entityType: effectiveFilters.entityType } : {}),
            ...(effectiveFilters.dateFrom ? { dateFrom: effectiveFilters.dateFrom } : {}),
            ...(effectiveFilters.dateTo ? { dateTo: effectiveFilters.dateTo } : {}),
            ...(effectiveFilters.search ? { search: effectiveFilters.search } : {}),
          },
        });

        if (!cancelled) {
          const payload = response.data ?? {};
          setEntries(Array.isArray(payload.entries) ? payload.entries : []);
          setMeta(payload.meta ?? null);
          setStats(payload.stats ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message ?? err?.message ?? "Failed to load audit logs.");
          setMeta(null);
          setStats(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAuditLog();

    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    refreshTick,
    effectiveFilters.actor,
    effectiveFilters.action,
    effectiveFilters.entityType,
    effectiveFilters.dateFrom,
    effectiveFilters.dateTo,
    effectiveFilters.search,
  ]);

  const columnCount = 4;

  const pageCount = Math.max(1, Number(meta?.pageCount ?? 1));

  return (
    <div className="admin-page admin-stack-lg audit-log-page">
      <section className="audit-hero admin-surface">
        <div className="audit-hero__intro">
          <span className="audit-hero__eyebrow">Security Workspace</span>
          <h1>Audit Log</h1>
          <p className="admin-muted">
            Every automated action and manual override is captured here to meet compliance requirements.
          </p>
        </div>
        <div className="audit-hero__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
            onClick={() => {
              setPage(1);
              setRefreshTick((value) => value + 1);
            }}
            disabled={loading}
            aria-label="Refresh audit log"
            title="Refresh audit log"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </section>

      <section className="admin-surface admin-stack-md audit-workspace">
        <header className="admin-row">
          <div className="audit-header-flush">
            <h2 className="admin-heading-with-icon">
              <ShieldAlert size={18} aria-hidden="true" />
              <span>Recent entries</span>
            </h2>
            <p className="admin-muted">Live audit trail from automation decisions and admin overrides.</p>
          </div>
          {stats ? (
            <div className="admin-pill-group">
              <span className="admin-pill">Total: {stats.total ?? 0}</span>
              {meta?.page ? <span className="admin-pill">Page {meta.page} of {pageCount}</span> : null}
            </div>
          ) : null}
        </header>

        <div className="admin-row audit-controls-row" role="region" aria-label="Audit log controls">
          <label className="audit-controls__search-field">
            <div className="audit-controls__search-card">
              <Search size={18} color="#64748b" aria-hidden="true" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search action, entity, admin, email, or IP"
                className="audit-controls__search-input"
                autoComplete="off"
                maxLength={120}
                aria-label="Search audit logs"
              />
            </div>
          </label>

          <div className="audit-controls__filter-field">
            <div className="id-card-queue__filter-button-wrap">
              <button type="button" className="admin-button admin-button--ghost audit-controls__filter-button" aria-hidden="true" tabIndex={-1}>
                <Filter size={16} aria-hidden="true" /> {actorFilterLabel}
              </button>
              <select
                className="id-card-queue__filter-select-overlay"
                value={filters.actor}
                onChange={handleFilterChange("actor")}
                aria-label="Filter by actor"
                title="Filter by actor"
              >
                {ACTOR_FILTER_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="audit-controls__filter-field">
            <button
              type="button"
              className="admin-button admin-button--ghost"
              onClick={() => setShowAdvancedFilters((current) => !current)}
              aria-expanded={showAdvancedFilters}
              aria-controls="audit-advanced-filters"
            >
              <SlidersHorizontal size={14} aria-hidden="true" /> {showAdvancedFilters ? "Hide" : "More"} filters
            </button>
          </div>
        </div>

        {showAdvancedFilters ? (
          <div className="audit-filters" id="audit-advanced-filters" role="group" aria-label="Advanced audit filters">
            <label className="admin-field">
              <span>Action</span>
              <select className="admin-select" value={filters.action} onChange={handleFilterChange("action")}>
                <option value="">All actions</option>
                {filterOptions.actions.map((action) => (
                  <option key={action} value={action}>
                    {action.replace(/[-_]/g, " ")}
                  </option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span>Entity</span>
              <select className="admin-select" value={filters.entityType} onChange={handleFilterChange("entityType")}>
                <option value="">All entities</option>
                {filterOptions.entities.map((entityType) => (
                  <option key={entityType} value={entityType}>
                    {entityType}
                  </option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span>From</span>
              <input
                type="date"
                className="admin-input"
                value={filters.dateFrom}
                onChange={handleFilterChange("dateFrom")}
                max={filters.dateTo || undefined}
              />
            </label>

            <label className="admin-field">
              <span>To</span>
              <input
                type="date"
                className="admin-input"
                value={filters.dateTo}
                onChange={handleFilterChange("dateTo")}
                min={filters.dateFrom || undefined}
              />
            </label>

            <div className="audit-filters__actions">
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
              >
                <X size={14} aria-hidden="true" /> Clear filters
              </button>
            </div>
          </div>
        ) : null}

        <div className="audit-filters__meta" aria-live="polite">
          {isDebouncingSearch ? <span className="admin-muted">Searching...</span> : null}
          {hasActiveFilters ? <span className="admin-muted">Filters active</span> : null}
        </div>

        {error ? <div className="admin-alert is-error">{error}</div> : null}
        <div className="admin-table-card audit-table-surface">
          <div className="admin-table-wrapper admin-scroll audit-table-wrapper">
            <table className="admin-table audit-table">
              <colgroup>
                <col className="audit-col-date" />
                <col className="audit-col-admin" />
                <col className="audit-col-action" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {loading && entries.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="admin-muted">
                      Loading audit logs…
                    </td>
                  </tr>
                ) : null}
                {!loading && entries.length === 0 && !error ? (
                  <tr>
                    <td colSpan={columnCount} className="admin-empty">
                      No audit entries found for the selected criteria.
                    </td>
                  </tr>
                ) : null}

                {entries.map((entry) => {
                  const tone = actionTone[entry.action] || "is-blue";
                  const description = getActionDescription(entry);
                  const actionLabel = entry.action.replace(/[-_]/g, " ");
                  const displayRoles = resolveDisplayRoles(entry);
                  const primaryRole = displayRoles[0] ?? null;
                  const roleVisual = primaryRole ? ROLE_VISUALS[primaryRole] : null;
                  const RoleIcon = roleVisual?.icon;
                  const overflowCount = Math.max(0, displayRoles.length - 1);
                  const allRolesLabel = displayRoles.join(", ");
                  const overflowRolesLabel = displayRoles.slice(1).join(", ");

                  return (
                    <tr key={entry.id}>
                      <td className="audit-table__when">{formatTimestamp(entry.timestamp)}</td>
                      <td className="audit-table__actor">
                        <div className="admin-stack-xxs">
                          <strong>{entry.actorName || entry.actor}</strong>
                          {roleVisual && RoleIcon ? (
                            <div className="audit-role-stack" title={allRolesLabel || undefined}>
                              <span className={`audit-role-badge ${roleVisual.className}`.trim()}>
                                <RoleIcon size={12} aria-hidden="true" />
                                <span>{roleVisual.label}</span>
                              </span>
                              {overflowCount > 0 ? (
                                <span
                                  className="audit-role-badge audit-role-badge--overflow"
                                  aria-label={`${overflowCount} more roles`}
                                  title={overflowRolesLabel || undefined}
                                  data-tooltip={overflowRolesLabel || undefined}
                                >
                                  +{overflowCount}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td className="audit-table__action">
                        <span className={`admin-chip ${tone}`.trim()}>{actionLabel}</span>
                      </td>
                      <td className="audit-table__detail">
                        <div className="admin-stack-xxs">
                          <span>{description}</span>
                          {entry.reason ? <span className="audit-table__muted">Reason: {entry.reason}</span> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-row audit-pagination audit-pagination-surface" role="navigation" aria-label="Audit log pagination">
          <span className="admin-muted audit-pagination__status">
            Page {page} of {pageCount}
          </span>
          <span className="admin-pill is-blue">
            Showing {entries.length} of {totalEntries}
          </span>
          <div className="admin-actions">
            <button
              type="button"
              className="admin-button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={loading || page <= 1}
            >
              <ChevronLeft size={16} aria-hidden="true" /> Previous
            </button>
            <button
              type="button"
              className="admin-button"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={loading || page >= pageCount}
            >
              Next <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
